import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@cloudscape-design/components';
import { handleNavigation } from '../../../utils/navigationUtils';
import './button.scss';

export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  icon?: string;
  iconPosition?: 'leading' | 'trailing';
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  useGradient?: boolean;
  href?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'leading',
  loading = false,
  disabled = false,
  onClick,
  className = '',
  type = 'button',
  useGradient = false,
  href,
}) => {
  const navigate = useNavigate();
  const buttonClasses = [
    'finspace-button',
    `finspace-button--${variant}`,
    `finspace-button--${size}`,
    useGradient && 'finspace-button--gradient',
    loading && 'finspace-button--loading',
    disabled && 'finspace-button--disabled',
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }

    if (href) {
      handleNavigation({ href, event: e, navigate, onBeforeNavigate: onClick });
    } else if (onClick) {
      onClick();
    }
  };

  const content = (
    <>
      {loading ? (
        <div className="finspace-button__spinner" />
      ) : (
        <>
          {icon && iconPosition === 'leading' && (
            <Icon name={icon as any} size="small" />
          )}
          <span className="finspace-button__text">{children}</span>
          {icon && iconPosition === 'trailing' && (
            <Icon name={icon as any} size="small" />
          )}
        </>
      )}
    </>
  );

  const commonStyle: React.CSSProperties = {
    display: 'inline-flex',
    verticalAlign: 'middle',
    lineHeight: 'normal',
  };

  // If href is provided, render as an anchor tag styled as a button
  if (href) {
    return (
      <a
        href={disabled || loading ? undefined : href}
        className={buttonClasses}
        onClick={handleClick}
        aria-disabled={disabled || loading}
        tabIndex={disabled || loading ? -1 : undefined}
        style={{ ...commonStyle, textDecoration: 'none' }}
      >
        {content}
      </a>
    );
  }

  // Otherwise, render as a button
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      style={commonStyle}
    >
      {content}
    </button>
  );
};
